// project imports
import { hasModuleAccess } from 'utils/roleWeights';

// assets
import {
  IconOrgStructure,
  IconControls,
  IconAudit,
  IconAdminPanel,
  IconSend,
  IconSupervisor,
  IconTeam,
  IconGroupAdd,
  IconDocs,
  IconSecurityMeasures,
  IconPermissionsGuide,
  IconDocsMessages,
  IconClients,
  IconShieldCheck,
  IconMail,
  IconClock
} from 'utils/icons';

// icons
const icons = {
  IconOrgStructure,
  IconControls,
  IconAudit,
  IconAdminPanel,
  IconSend,
  IconSupervisor,
  IconTeam,
  IconGroupAdd,
  IconDocs,
  IconSecurityMeasures,
  IconPermissionsGuide,
  IconDocsMessages,
  IconClients,
  IconShieldCheck,
  IconMail,
  IconClock
};

// ==============================|| MENU ITEMS - ADMIN ||============================== //
// Each child carries `access`, one of:
//  - { moduleKey, minRole } - generic core_modules-backed check
//    (hasModuleAccess(), utils/roleWeights.js) - matches exactly what
//    RequireModuleAccess (utils/route-guard/) enforces on the route.
//  - { adminOnly: true } - superadmin OR delegated group-admin
//    (core_group_admins DELEGATION, not a module_key grant - see
//    utils/route-guard/RequireAdminAccess.jsx's docblock for why this stays
//    separate). 2026-08-29: extended from Uživatelé/Skupiny to also cover
//    Role/Sekce a funkce - a TC-level group-admin needs to SEE both catalogs
//    to pick an existing role to attach to their own group (Skupiny page),
//    but creating/editing/deleting a role or module definition itself stays
//    superadmin-only (enforced separately, in Roles.jsx/Modules.jsx AND at
//    the route level - see AdminRoleController/AdminModuleController).
// buildAdminMenu() filters by these before rendering (2026-08-21/22 - a
// real second user account exposed that this menu was shown to everyone
// regardless of role, see docs/ARCHITECTURE.md).

const ADMIN_ITEMS = [
  {
    id: 'admin-registration',
    title: 'Audit / WhiteList',
    type: 'item',
    url: '/admin/registration',
    icon: icons.IconAudit,
    breadcrumbs: false,
    access: { adminOnly: true }
  },
  {
    id: 'admin-users',
    title: 'Uživatelé',
    type: 'item',
    url: '/admin/users',
    icon: icons.IconTeam,
    breadcrumbs: false,
    access: { adminOnly: true }
  },
  {
    id: 'admin-roles',
    title: 'Role',
    type: 'item',
    url: '/admin/roles',
    icon: icons.IconOrgStructure,
    breadcrumbs: false,
    access: { adminOnly: true }
  },
  {
    id: 'admin-groups',
    title: 'Skupiny',
    type: 'item',
    url: '/admin/groups',
    icon: icons.IconGroupAdd,
    breadcrumbs: false,
    access: { adminOnly: true }
  },
  {
    id: 'admin-modules',
    title: 'Sekce a funkce',
    type: 'item',
    url: '/admin/modules',
    icon: icons.IconControls,
    breadcrumbs: false,
    access: { adminOnly: true }
  },
  {
    id: 'admin-tenants',
    title: 'Klienti',
    type: 'item',
    url: '/admin/tenants',
    icon: icons.IconClients,
    breadcrumbs: false,
    access: { moduleKey: 'system', minRole: 'admin' }
  },
  {
    id: 'admin-trusted-networks',
    title: 'Důvěryhodné sítě',
    type: 'item',
    url: '/admin/trusted-networks',
    icon: icons.IconShieldCheck,
    breadcrumbs: false,
    access: { moduleKey: 'system', minRole: 'admin' }
  }
];

// Samostatná sekce "Manažeři" (2026-08-29, na výslovné přání uživatele) -
// pro operativní věci, které potřebuje i člověk BEZ delegace group-admina
// (core_group_admins), jen s přímým core_modules oprávněním - typicky
// "Push - odesílatel"/"Push - správce" (push:editor+) bez toho, aby byl
// zároveň group-admin. "Push zprávy" se sem přesunula ze "Správce" (odtud
// pryč, protože sekce jménem "Správce" matla někoho, kdo vůbec admin není -
// jen posílá zprávy). Zbytek "Správce" (Role/Skupiny/Sekce a funkce/...)
// zůstává beze změny - i nadále vidí superadmin i libovolný delegovaný
// group-admin (adminOnly), viz ADMIN_ITEMS výše.
//
// "Docházkový systém" dřív (2026-08-30 - 2026-09-02) žil taky tady jako
// vnořený collapse (buildAttendanceManagerCollapse() v menu-items/attendance.jsx).
// 2026-09-02, na výslovné přání uživatele - zrušeno, všechny odkazy
// přesunuty do buildAttendanceDashboardMenu() (vlastní top-level skupina
// "Docházkový systém", rozdělená podle role - viz menu-items/attendance.jsx).
const MANAGER_ITEMS = [
  {
    id: 'admin-push',
    title: 'Push zprávy',
    type: 'item',
    url: '/admin/push',
    icon: icons.IconSend,
    breadcrumbs: false,
    access: { moduleKey: 'push', minRole: 'editor' }
  }
];

// Samostatná sekce "Dokumentace" (2026-08-23, na výslovné přání uživatele) -
// čistě informační stránky (nic tady nejde nastavit), proto mají vlastní
// menu skupinu odděleně od "Správce".
//
// 2026-08-29 (druhá úprava, "dokumentace by měla mít svoje role") - ne
// plošně adminOnly pro všech pět, ale každá stránka podle SVÉHO tématu,
// stejný princip jako filtrování katalogu Role/Sekce a funkce výše (vidět
// jen to, co je k dané funkci reálně relevantní):
//  - Zabezpečení/Přístupy zůstávají adminOnly - vysvětlují mechanismus
//    session/RBAC, který se týká i delegovaného group-admina samotného
//    (Přístupy má dokonce vlastní kapitolu "Delegovaný správce skupiny"),
//    ne konkrétní funkční modul.
//  - Centrum zpráv/Docházkový systém jsou svázané na skutečný modul
//    (push/attendance) - group-admin bez odpovídající role je neuvidí,
//    přesně jak "Sekce a funkce" schovává "Docházkový systém" klientovi,
//    který k němu nemá žádnou roli.
//  - E-mail zůstává superadmin-only (moduleKey system) - popisuje
//    provozní/serverovou konfiguraci (DKIM/SPF/DMARC), klient na ni nemá
//    žádný vliv ani důvod ji vidět.
const DOCS_ITEMS = [
  {
    id: 'docs-security',
    title: 'Zabezpečení',
    type: 'item',
    url: '/admin/docs/security',
    icon: icons.IconSecurityMeasures,
    breadcrumbs: false,
    access: { adminOnly: true }
  },
  {
    id: 'docs-permissions',
    title: 'Přístupy',
    type: 'item',
    url: '/admin/docs/permissions',
    icon: icons.IconPermissionsGuide,
    breadcrumbs: false,
    access: { adminOnly: true }
  },
  {
    id: 'docs-messages',
    title: 'Centrum zpráv',
    type: 'item',
    url: '/admin/docs/messages',
    icon: icons.IconDocsMessages,
    breadcrumbs: false,
    access: { moduleKey: 'push', minRole: 'reader' }
  },
  {
    id: 'docs-email',
    title: 'E-mail',
    type: 'item',
    url: '/admin/docs/email',
    icon: icons.IconMail,
    breadcrumbs: false,
    access: { moduleKey: 'system', minRole: 'admin' }
  },
  {
    id: 'docs-attendance',
    title: 'Docházkový systém',
    type: 'item',
    url: '/admin/docs/attendance',
    icon: icons.IconClock,
    breadcrumbs: false,
    access: { moduleKey: 'attendance', minRole: 'reader' }
  }
];

/**
 * Returns the "Administrace" menu group filtered to what `user` (the full
 * /auth/me payload) actually allows, or null if nothing is visible - the
 * whole "Správce" section then simply doesn't render, rather than showing
 * an empty collapse.
 */
export function buildAdminMenu(user) {
  const isAdminDelegate = Boolean(user?.admin_access?.is_system_admin || user?.admin_access?.is_group_admin);

  const children = ADMIN_ITEMS.filter((item) =>
    item.access.adminOnly ? isAdminDelegate : hasModuleAccess(user?.permissions, item.access.moduleKey, item.access.minRole)
  );

  const managerChildren = MANAGER_ITEMS.filter((item) =>
    item.access.adminOnly ? isAdminDelegate : hasModuleAccess(user?.permissions, item.access.moduleKey, item.access.minRole)
  );

  const docsChildren = DOCS_ITEMS.filter((item) =>
    item.access.adminOnly ? isAdminDelegate : hasModuleAccess(user?.permissions, item.access.moduleKey, item.access.minRole)
  );

  const groups = [];
  if (managerChildren.length > 0) {
    groups.push({ id: 'manager-collapse', title: 'Manažeři', type: 'collapse', icon: icons.IconSupervisor, children: managerChildren });
  }
  if (children.length > 0) {
    groups.push({ id: 'admin-collapse', title: 'Správce', type: 'collapse', icon: icons.IconAdminPanel, children });
  }
  if (docsChildren.length > 0) {
    groups.push({ id: 'docs-collapse', title: 'Dokumentace', type: 'collapse', icon: icons.IconDocs, children: docsChildren });
  }

  if (groups.length === 0) return null;

  // Skupinový "sidebar_label" banner (core_groups.sidebar_label, migrace 034)
  // 2026-08-29, na výslovné přání uživatele - dřív se zobrazoval jako
  // samostatný řádek NAD "ADMINISTRACE" (Navigation/index.jsx), teď rovnou
  // NAHRAZUJE titulek "ADMINISTRACE" pro členy skupiny, která ho má
  // nastavený (např. "TC BOHEMIA" pro @tcbohemia.com) - žádné duplicitní
  // zobrazení stejné informace na dvou místech.
  // [...new Set(...)] (2026-08-30, real bug fixed) - uživatel může být PŘÍMÝM
  // členem více skupin v téže větvi zároveň (např. "vedoucí pobočky" i
  // "zaměstnanci" současně - group-derived role model to nezakazuje), obě
  // bez vlastního sidebar_label zdědí STEJNÝ label od nejbližšího předka, co
  // ho má (GroupRepository::groupsForUser()) - bez deduplikace se stejný
  // banner ("TC BOHEMIA") zobrazil tolikrát, kolika skupinami k němu
  // uživatel dospěl, ne jednou za skutečně odlišný label.
  const banners = [...new Set((user?.groups || []).filter((g) => g.sidebar_label).map((g) => g.sidebar_label))];
  const title = banners.length > 0 ? banners.join(' / ') : 'ADMINISTRACE';
  // Malá ikonka vedle banneru (2026-08-29, core_tenants.icon_url, migrace
  // 055, na výslovné přání uživatele) - jen když se banner reálně zobrazuje
  // (ne u obecného "ADMINISTRACE"), a jen když ho appka má (může se stát, že
  // klient má sidebar_label, ale žádnou ikonu ještě nenahrál). Potvrzeno
  // 2026-08-31 (po prvním reálném nasazení dat), že tohle logo klienta tu
  // zůstává záměrně - jen tlačítko offcanvas-right-user v hlavičce má
  // ukazovat obecnou ikonu uživatele, ne tohle.
  const titleIconUrl = banners.length > 0 ? user?.tenant_icon_url : null;

  return {
    id: 'group-admin',
    title,
    titleIconUrl,
    type: 'group',
    children: groups
  };
}
